import React, { useState, MouseEvent, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/utils/types";
import { RxCross2 as Cross } from "react-icons/rx";
import { Link } from "react-router-dom";
import NotificationToast from "../../../components/NotificationToast";
import { authSelector } from "../../../redux/modules/auth/userSelector";
import { getAllFavoriteFolders } from "../../../redux/modules/favorites/action";
import { favoriteFoldersCompanySelector } from "../../../redux/modules/favoritesCompany/selectors";
import { addCompanyInFolderFavoriteFoldersCompany, getFavoriteFoldersIdCompany, removeCompanyInFolder } from "../../../redux/modules/favoritesCompany/action";

export default function FavoritesFolderDropdown(props: {
    favoriteFolders: { id: number; folderName: string; companyName: string }[];
    handleDragOver: (e: React.DragEvent) => void;
    handleShow: (folder: string | null) => void;
    removeFavoriteFolder: (folderName: string) => void;
}) {
    const {
        favoriteFolders,
        handleDragOver,
        handleShow,
        removeFavoriteFolder,
    } = props;

    const dispatch = useDispatch<any>();
    const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const anchorRef = useRef<HTMLLIElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [panelPos, setPanelPos] = useState({ top: 12, left: 326 });
    const [notification, setNotification] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
    const { user } = useSelector(authSelector);

    useEffect(() => {
        if (dropdownOpen) {
            dispatch(getAllFavoriteFolders());
        }
    }, [dropdownOpen, dispatch]);

    useEffect(() => {
        if (selectedFolder) {
            const folderId = favoriteFolders.find(
                (folder) => folder.folderName === selectedFolder
            )?.id;
            if (folderId) {
                dispatch(getFavoriteFoldersIdCompany(folderId));
            }
        }
    }, [selectedFolder, dispatch, favoriteFolders]);

    const selectedFolderId = favoriteFolders.find(
        (folder) => folder.folderName === selectedFolder
    )?.id;

    const companies = useSelector((state: RootState) => {
        const favoriteFoldersCompanyState = state.favoriteFoldersCompany || {};
        return favoriteFoldersCompanyState.companies?.[selectedFolderId ?? -1] || [];
    });

    const { status, message } = useSelector(favoriteFoldersCompanySelector);

    useEffect(() => {
        if (status === 'success' || status === 'failed') {
           // setNotification({ show: true, message: message || '' });
            const timer = setTimeout(() => {
                setNotification({ show: false, message: '' });
                dispatch({ type: 'favoriteFoldersCompany/reset' }); // Ensure to reset the status and message
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [status, message, dispatch]);

    const placePanel = useCallback(() => {
        const sidebar = document.querySelector(".vertical-nav");
        const sideRect = sidebar?.getBoundingClientRect();
        const anchorRect = anchorRef.current?.getBoundingClientRect();
        const top = Math.max(12, anchorRect?.top ?? 80);
        const left = (sideRect?.right ?? 318) + 8;
        setPanelPos({ top, left });
    }, []);

    const toggleDropdown = useCallback(
        (e: MouseEvent) => {
            e.preventDefault();
            setDropdownOpen((prev) => !prev);
        },
        []
    );

    useEffect(() => {
        if (!dropdownOpen) return;
        placePanel();
        const sidebar = document.querySelector(".vertical-nav");
        window.addEventListener("resize", placePanel);
        sidebar?.addEventListener("scroll", placePanel);
        const onPointerDown = (event: Event) => {
            const target = event.target as Node;
            if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
            setDropdownOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => {
            window.removeEventListener("resize", placePanel);
            sidebar?.removeEventListener("scroll", placePanel);
            document.removeEventListener("mousedown", onPointerDown);
        };
    }, [dropdownOpen, placePanel]);

    const handleFolderClick = useCallback((folder: string) => {
        setSelectedFolder((prev) => (prev === folder ? null : folder));
    }, []);

    const handleCompanyClick = useCallback((e: MouseEvent) => {
        e.stopPropagation();
    }, []);

    const handleRemoveCompany = useCallback(
        (folderId: number, companyId: number, e: React.MouseEvent) => {
            e.stopPropagation();
            dispatch(removeCompanyInFolder({ folderId, companyId }))
                .unwrap()
                .then(() => {
                    if (selectedFolder) {
                        const selectedFolderId = favoriteFolders.find(
                            (folder) => folder.folderName === selectedFolder
                        )?.id;
                        if (selectedFolderId) {
                            dispatch(getFavoriteFoldersIdCompany(selectedFolderId));
                        }
                    }
                    setNotification({ show: true, message: 'Company removed successfully' });
                    setTimeout(() => setNotification({ show: false, message: '' }), 3000);
                })
                .catch((error: any) => {
                    console.error("Failed to remove company:", error);
                    setNotification({ show: true, message: 'Failed to remove company' });
                    setTimeout(() => setNotification({ show: false, message: '' }), 3000);
                });
        },
        [dispatch, favoriteFolders, selectedFolder]
    );

    const handleDrop = async (e: React.DragEvent, folder: string) => {
        e.preventDefault();
        const companyName = e.dataTransfer.getData("text/plain");

        const folderObj = favoriteFolders.find((f) => f.folderName === folder);
        if (folderObj) {
            const folderId = folderObj.id;
            try {
                await dispatch(
                    addCompanyInFolderFavoriteFoldersCompany({ folderId, companyName })
                ).unwrap();
                dispatch(getAllFavoriteFolders());
                setNotification({ show: true, message: 'Company added successfully' });
                setTimeout(() => setNotification({ show: false, message: '' }), 3000);
            } catch (error) {
                console.error("Failed to add company to folder:", error);
                setNotification({ show: true, message: 'Failed to add company' });
                setTimeout(() => setNotification({ show: false, message: '' }), 3000);
            }
        } else {
            console.error("Folder not found");
        }

        setDropdownOpen(true);
    };

    // Filter folders based on companyName
    const filteredFolders = favoriteFolders.filter(folder => folder.companyName === user?.companyName);

    return (
        <>
            <li className="nav-item favorites-nav-item" ref={anchorRef}>
                <a
                    href="#!"
                    onClick={toggleDropdown}
                    className="nav-link text-dark"
                    aria-expanded={dropdownOpen}
                >
                    <i className="fa fa-folder mr-3 fa-fw text-dark"></i>
                    <span className="favorites-nav-label">Wendwise Company Favorites</span>
                </a>
                {dropdownOpen && createPortal(
                    <div
                        ref={panelRef}
                        className="favorites-side-panel"
                        role="region"
                        aria-label="Wendwise Company Favorites"
                        style={{ position: "fixed", top: panelPos.top, left: panelPos.left }}
                    >
                        <div className="favorites-navy-body">
                        {filteredFolders.length > 0 ? (
                            filteredFolders.map((folder) => {
                                const isOpen = selectedFolder === folder.folderName;
                                return (
                                <div
                                    key={folder.id}
                                    className={`favorites-navy-row${isOpen ? " is-open" : ""}`}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, folder.folderName)}
                                >
                                    <div className="favorites-navy-head">
                                        <div
                                            className="favorites-folder-main"
                                            onClick={() => handleFolderClick(folder.folderName)}
                                            role="button"
                                            tabIndex={0}
                                            aria-expanded={isOpen}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    handleFolderClick(folder.folderName);
                                                }
                                            }}
                                        >
                                            <span className="favorites-folder-name">{folder.folderName}</span>
                                        </div>
                                        <div className="favorites-folder-actions">
                                            <button
                                                type="button"
                                                onClick={() => handleShow(folder.folderName)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => removeFavoriteFolder(folder.folderName)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                    {isOpen && (
                                        <div className="favorites-navy-companies">
                                            {companies.length > 0 ? (
                                                companies.map((company) => (
                                                    <div
                                                        key={company.companyId}
                                                        className="favorites-company-row"
                                                    >
                                                        <Link
                                                            to={`/home/view/${company.companyId}`}
                                                            onClick={handleCompanyClick}
                                                            className="favorites-company-link"
                                                        >
                                                            {company.companyName}
                                                        </Link>
                                                        <Cross
                                                            className="text-danger"
                                                            role="button"
                                                            onClick={(e) => handleRemoveCompany(folder.id, company.companyId, e)}
                                                            title="Delete Company"
                                                        />
                                                    </div>
                                                ))
                                            ) : (
                                                <p className="favorites-empty">No companies available</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                                );
                            })
                        ) : (
                            <p className="favorites-empty">No folders available</p>
                        )}
                        </div>
                    </div>,
                    document.body
                )}
            </li>
            {notification.show && (
                <NotificationToast message={notification.message} show={notification.show} />
            )}
        </>
    );
}